import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Settings, Users } from 'lucide-react';
import AppBarMenu from './AppBarMenu';
import Logo from './Logo';
import InspectionsTab from './admin/InspectionsTab';
import SystemSettingsTab from './admin/SystemSettingsTab';
import UserManagementTab from './admin/UserManagementTab';

// Tab button component
const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors font-medium ${
      active
        ? 'border-primary text-primary'
        : 'border-transparent text-text-muted hover:text-text hover:border-border'
    }`}
  >
    <Icon size={18} />
    {children}
  </button>
);

const AdminDashboard = ({ onLogout, isAdmin = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Derive active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/admin/settings')) return 'settings';
    if (path.includes('/admin/users')) return 'users';
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  const handleTabChange = (tab) => {
    if (tab === 'dashboard') {
      navigate('/admin');
    } else {
      navigate(`/admin/${tab}`);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background pb-24 flex flex-col">
      {/* App Bar */}
      <header className="app-bar">
        <button onClick={handleBack} className="cursor-pointer hover:opacity-80">
          <Logo size={40} />
        </button>
        <h1 className="flex-1 text-white font-normal text-xl">
          {t('admin.title')}
        </h1>
        <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} />
      </header>

      {/* Tab Navigation */}
      <div className="border-b border-border bg-surface px-4 lg:px-8">
        <nav className="flex gap-2">
          <TabButton
            active={activeTab === 'dashboard'}
            onClick={() => handleTabChange('dashboard')}
            icon={LayoutDashboard}
          >
            {t('admin.tabs.dashboard')}
          </TabButton>
          <TabButton
            active={activeTab === 'settings'}
            onClick={() => handleTabChange('settings')}
            icon={Settings}
          >
            {t('admin.tabs.settings')}
          </TabButton>
          <TabButton
            active={activeTab === 'users'}
            onClick={() => handleTabChange('users')}
            icon={Users}
          >
            {t('admin.tabs.users')}
          </TabButton>
        </nav>
      </div>

      {/* Tab Content */}
      <main className="flex-1 w-full px-4 lg:px-8 py-6">
        {activeTab === 'dashboard' && <InspectionsTab />}
        {activeTab === 'settings' && <SystemSettingsTab />}
        {activeTab === 'users' && <UserManagementTab />}
      </main>
    </div>
  );
};

export default AdminDashboard;
